import { useEffect, useState } from 'react';

import { Button, Stack, TextField } from '@mui/material';

import * as UnidadeCondominialAPI from '../../../api/unidadeCondominial';
import * as UnidadeCondominialProdutoAPI from '../../../api/unidadeCondominialProduto';
import * as DataListAPI from '../../../api/dataList';
import { useNavigateParams } from '../../../hooks/useNavigateParams';
import useFlashMessage from '../../../hooks/useFlashMessage';
import SystemTabs from '../../../ui/SystemTabs';
import Checkbox from '../../form/Checkbox';
import DataList from '../../form/DataList';
import SelectEmpresa from '../../form/SelectEmpresa';

function formatarMoeda(value) {
    const numero = Number(value);

    if (!Number.isFinite(numero)) {
        return '';
    }

    return numero.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    });
}

function montarServicoSelecionado(dados) {
    if (dados?.ServicoTabela?.descricao) {
        return dados.ServicoTabela;
    }

    if (!dados?.Produto?.descricao) {
        return {};
    }

    return {
        produtoId: dados.produtoId,
        embalagemId: dados.embalagemId,
        codigo: dados.Produto.codigo,
        produtoDescricao: dados.Produto.descricao,
        embalagemDescricao: dados.Embalagem?.descricao || '',
        descricao: [dados.Produto.codigo, dados.Produto.descricao, dados.Embalagem?.descricao].filter(Boolean).join(' — '),
        aceitaFracao: dados.aceitaFracao ?? dados.Embalagem?.aceitaFracao ?? false,
        preco: dados.precoVigente ?? dados.preco,
    };
}

function Form({
    dados,
    setDados,
    handleSubmit,
    caminhoVoltar,
    OptionsEmpresa,
    editando,
    carregando,
    salvando,
    falhaCarregamento,
}) {
    const navigate = useNavigateParams();
    const { setFlashMessage } = useFlashMessage();

    const [optionsUnidade, setOptionsUnidade] = useState([]);
    const [optionsTabela, setOptionsTabela] = useState([]);
    const [optionsServico, setOptionsServico] = useState([]);

    const [unidade, setUnidade] = useState(dados?.UnidadeCondominial || {});

    const [tabela, setTabela] = useState(dados?.tabelaPreco || {});

    const [servico, setServico] = useState(montarServicoSelecionado(dados));

    useEffect(() => {
        setUnidade(dados?.UnidadeCondominial || {});
    }, [dados?.UnidadeCondominial]);

    useEffect(() => {
        setTabela(dados?.tabelaPreco || {});
    }, [dados?.tabelaPreco]);

    useEffect(() => {
        setServico(montarServicoSelecionado(dados));
    }, [
        dados?.ServicoTabela,
        dados?.Produto,
        dados?.Embalagem,
        dados?.produtoId,
        dados?.embalagemId,
        dados?.precoVigente,
        dados?.preco,
        dados?.aceitaFracao,
    ]);

    async function consultarUnidades(filtro = '') {
        if (!dados?.empresaId) {
            setOptionsUnidade([]);
            return [];
        }

        try {
            const result = await DataListAPI.dataList({
                empresaId: dados.empresaId,
                tabela: 'UnidadeCondominial',
                filtro,
            });

            const opcoes = Array.isArray(result) ? result : [];

            setOptionsUnidade(opcoes);

            return opcoes;
        } catch (error) {
            setOptionsUnidade([]);

            setFlashMessage(
                error.response?.data?.message || 'Não foi possível consultar as unidades condominiais.',
                error.response?.data?.type || 'error'
            );

            return [];
        }
    }

    async function consultarTabelas(filtro = '') {
        if (!dados?.empresaId) {
            setOptionsTabela([]);
            return [];
        }

        try {
            const response = await UnidadeCondominialProdutoAPI.listarTabelasDisponiveis({
                empresaId: dados.empresaId,
                filtro,
                limite: 100,
            });

            const result = response.data?.data || [];

            setOptionsTabela(result);

            return result;
        } catch (error) {
            setOptionsTabela([]);

            setFlashMessage(
                error.response?.data?.message || 'Não foi possível consultar as tabelas de preço de serviços.',
                error.response?.data?.type || 'error'
            );

            return [];
        }
    }

    async function consultarServicos(filtro = '') {
        if (!dados?.empresaId || !dados?.tabelaPrecoId) {
            setOptionsServico([]);
            return [];
        }

        try {
            const response = await UnidadeCondominialProdutoAPI.listarServicosDisponiveis({
                empresaId: dados.empresaId,
                tabelaPrecoId: dados.tabelaPrecoId,
                filtro,
                limite: 100,
            });

            const result = response.data?.data || [];

            setOptionsServico(result);

            return result;
        } catch (error) {
            setOptionsServico([]);

            setFlashMessage(
                error.response?.data?.message || 'Não foi possível consultar os serviços disponíveis.',
                error.response?.data?.type || 'error'
            );

            return [];
        }
    }

    function limparTabelaEServico() {
        setTabela({});
        setServico({});
        setOptionsTabela([]);
        setOptionsServico([]);
    }

    function limparServico() {
        setServico({});
        setOptionsServico([]);
    }

    async function handleChange(event) {
        const { name, value, type, checked } = event.target;

        if (name === 'empresaId') {
            setUnidade({});
            setOptionsUnidade([]);
            limparTabelaEServico();

            setDados((anterior) => ({
                ...anterior,
                empresaId: value,
                unidadeCondominialId: null,
                tabelaPrecoId: null,
                produtoId: null,
                embalagemId: null,
                UnidadeCondominial: {},
                tabelaPreco: {},
                Produto: {},
                Embalagem: {},
                ServicoTabela: {},
            }));

            return;
        }

        if (name === 'UnidadeCondominial.descricao') {
            const valorSelecionado = String(value || '').trim();

            const opcaoSelecionada = optionsUnidade.find((opcao) => String(opcao?.descricao || '').trim() === valorSelecionado);

            if (opcaoSelecionada) {
                setUnidade(opcaoSelecionada);

                setDados((anterior) => ({
                    ...anterior,
                    unidadeCondominialId: Number(opcaoSelecionada.id),
                    UnidadeCondominial: opcaoSelecionada,
                }));

                return;
            }

            setUnidade({
                descricao: valorSelecionado,
            });

            setDados((anterior) => ({
                ...anterior,
                unidadeCondominialId: null,
                UnidadeCondominial: {
                    descricao: valorSelecionado,
                },
            }));

            await consultarUnidades(valorSelecionado);

            return;
        }

        if (name === 'tabelaPreco.descricao') {
            setTabela((anterior) => ({
                ...anterior,
                descricao: value,
            }));

            const opcoes = await consultarTabelas(value);

            const opcaoSelecionada = opcoes.find((opcao) => String(opcao.descricao) === String(value));

            limparServico();

            setDados((anterior) => ({
                ...anterior,
                tabelaPrecoId: opcaoSelecionada?.id ? Number(opcaoSelecionada.id) : null,
                produtoId: null,
                embalagemId: null,
                tabelaPreco: opcaoSelecionada || {
                    descricao: value,
                },
                Produto: {},
                Embalagem: {},
                ServicoTabela: {},
            }));

            return;
        }

        if (name === 'ServicoTabela.descricao') {
            const valorSelecionado = String(value || '').trim();

            /*
             * O DataList emite exatamente option.descricao.
             * A seleção precisa ser localizada antes de executar
             * uma nova consulta.
             */
            const opcaoSelecionada = optionsServico.find((opcao) => String(opcao?.descricao || '').trim() === valorSelecionado);

            if (opcaoSelecionada) {
                setServico(opcaoSelecionada);

                setDados((anterior) => ({
                    ...anterior,

                    produtoId: Number(opcaoSelecionada.produtoId),

                    embalagemId: Number(opcaoSelecionada.embalagemId),

                    Produto: {
                        id: Number(opcaoSelecionada.produtoId),
                        codigo: opcaoSelecionada.codigo,
                        descricao: opcaoSelecionada.produtoDescricao,
                    },

                    Embalagem: {
                        id: Number(opcaoSelecionada.embalagemId),
                        descricao: opcaoSelecionada.embalagemDescricao,
                        aceitaFracao: Boolean(opcaoSelecionada.aceitaFracao),
                    },

                    ServicoTabela: opcaoSelecionada,

                    aceitaFracao: Boolean(opcaoSelecionada.aceitaFracao),

                    precoVigente: opcaoSelecionada.preco ?? null,
                }));

                return;
            }

            /*
             * Não corresponde a uma opção: o usuário ainda
             * está digitando. Limpamos as chaves anteriores
             * e atualizamos a pesquisa.
             */
            setServico({
                descricao: valorSelecionado,
            });

            setDados((anterior) => ({
                ...anterior,
                produtoId: null,
                embalagemId: null,
                Produto: {},
                Embalagem: {},
                ServicoTabela: {
                    descricao: valorSelecionado,
                },
                aceitaFracao: false,
                precoVigente: null,
            }));

            await consultarServicos(valorSelecionado);

            return;
        }

        if (name === 'dataFim' && !value) {
            setDados((anterior) => ({
                ...anterior,
                dataFim: '',
            }));

            return;
        }

        setDados((anterior) => ({
            ...anterior,
            [name]: type === 'checkbox' ? checked : value,
        }));
    }

    async function handleOnFocus(event) {
        if (editando) return;

        if (event.target.name === 'UnidadeCondominial.descricao') {
            await consultarUnidades(event.target.value);
        }

        if (event.target.name === 'tabelaPreco.descricao') {
            await consultarTabelas(event.target.value);
        }

        if (event.target.name === 'ServicoTabela.descricao') {
            await consultarServicos(event.target.value);
        }
    }

    function submit(event) {
        event.preventDefault();

        handleSubmit(dados);
    }

    const aceitaFracao = Boolean(servico?.aceitaFracao ?? dados?.aceitaFracao ?? dados?.Embalagem?.aceitaFracao);

    const tabServico = (
        <Stack spacing={2}>
            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                <SelectEmpresa
                    name="empresaId"
                    text="Empresa"
                    options={OptionsEmpresa}
                    handleOnChange={handleChange}
                    value={dados?.empresaId || ''}
                    disabled={editando}
                    required
                    notOptNull={false}
                />

                <DataList
                    text="Unidade condominial"
                    name="UnidadeCondominial.descricao"
                    inputWidth="500px"
                    filteredOptions={optionsUnidade}
                    handleOnChange={handleChange}
                    handleOnFocus={handleOnFocus}
                    value={unidade?.descricao || ''}
                    readonly={editando || !dados?.empresaId}
                    required
                />
            </Stack>

            <DataList
                text="Tabela de preços"
                name="tabelaPreco.descricao"
                inputWidth="400px"
                filteredOptions={optionsTabela}
                handleOnChange={handleChange}
                handleOnFocus={handleOnFocus}
                value={tabela?.descricao || ''}
                readonly={editando || !dados?.empresaId}
                required
            />

            <DataList
                text="Serviço"
                name="ServicoTabela.descricao"
                inputWidth="650px"
                filteredOptions={optionsServico}
                handleOnChange={handleChange}
                handleOnFocus={handleOnFocus}
                value={servico?.descricao || ''}
                readonly={editando || !dados?.empresaId || !dados?.tabelaPrecoId}
                required
            />

            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                <TextField
                    name="quantidade"
                    label="Quantidade"
                    type="number"
                    value={dados?.quantidade ?? ''}
                    onChange={handleChange}
                    inputProps={{
                        min: 0.001,
                        step: aceitaFracao ? 0.001 : 1,
                    }}
                    helperText={
                        aceitaFracao
                            ? 'Aceita quantidade fracionada com até três casas decimais.'
                            : 'A embalagem aceita somente quantidade inteira.'
                    }
                    required
                />

                <TextField
                    label="Preço vigente"
                    value={formatarMoeda(servico?.preco ?? dados?.precoVigente)}
                    InputProps={{
                        readOnly: true,
                    }}
                />
            </Stack>

            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                <TextField
                    name="dataInicio"
                    label="Início da vigência"
                    type="date"
                    value={dados?.dataInicio || ''}
                    onChange={handleChange}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    required
                />

                <TextField
                    name="dataFim"
                    label="Término da vigência"
                    type="date"
                    value={dados?.dataFim || ''}
                    onChange={handleChange}
                    InputLabelProps={{
                        shrink: true,
                    }}
                />
            </Stack>

            <Checkbox
                title="Indica que o serviço está ativo para a unidade condominial"
                text="Ativo"
                name="ativo"
                handleOnChange={handleChange}
                value={dados?.ativo}
                checked={Boolean(dados?.ativo)}
            />

            <TextField
                name="observacao"
                label="Observação"
                value={dados?.observacao || ''}
                onChange={handleChange}
                multiline
                minRows={3}
                fullWidth
            />
        </Stack>
    );

    const tabsConfig = [
        {
            key: 'servico',
            label: 'Serviço',
            title: 'Serviço vinculado à unidade condominial',
            panel: tabServico,
        },
    ];

    return (
        <form onSubmit={submit} autoComplete="off">
            <h1>{editando ? 'EDIÇÃO DO SERVIÇO DA UNIDADE' : 'CADASTRO DO SERVIÇO DA UNIDADE'}</h1>

            <SystemTabs tabs={tabsConfig} initialKey="servico" />

            <Stack
                direction="row"
                spacing={1}
                sx={{
                    mt: 2,
                }}
            >
                <Button
                    variant="contained"
                    color="secondary"
                    type="button"
                    disabled={salvando}
                    onClick={() => navigate(caminhoVoltar)}
                >
                    Voltar
                </Button>

                <Button type="submit" variant="contained" color="success" disabled={carregando || salvando || falhaCarregamento}>
                    {salvando ? 'Salvando...' : editando ? 'Atualizar' : 'Cadastrar'}
                </Button>
            </Stack>
        </form>
    );
}

export default Form;
